import React, { useState, useEffect, useRef } from 'react';
import { DonationRecord } from '../../types';
import { getDonationsDB } from '../../services/db';
import { X, Sparkles, Heart } from 'lucide-react';
import { formatCurrencyMYR } from '../../utils/formatters';

const INITIAL_DELAY_MS = 3500; // 3.5 seconds initial delay before first popup
const DISPLAY_DURATION_MS = 5000; // 5 seconds on screen
const QUIET_INTERVAL_MS = 14000; // 14 seconds quiet interval between popups
const MAX_SESSION_APPEARANCES = 5; // Maximum 5 times per session

interface DonationLiveToastPopupProps {
  hasBottomNav?: boolean;
}

export const DonationLiveToastPopup: React.FC<DonationLiveToastPopupProps> = ({ hasBottomNav }) => {
  const [donations, setDonations] = useState<DonationRecord[]>([]);
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [isDismissed, setIsDismissed] = useState<boolean>(() => {
    try {
      return sessionStorage.getItem('asmm_live_toast_dismissed') === 'true';
    } catch {
      return false;
    }
  });
  const [isPaused, setIsPaused] = useState<boolean>(false);

  const appearanceCountRef = useRef<number>(0);
  const cycleTimeoutRef = useRef<any>(null);

  const loadData = async () => {
    try {
      const all = await getDonationsDB();
      const successful = all.filter((d) => d.status === 'Berjaya');
      if (successful.length > 0) {
        setDonations(successful);
      }
    } catch { }
  };

  useEffect(() => {
    loadData();
    const handleUpdate = () => loadData();
    window.addEventListener('asmm_db_updated', handleUpdate);
    window.addEventListener('asmm_donation_received', handleUpdate);
    return () => {
      window.removeEventListener('asmm_db_updated', handleUpdate);
      window.removeEventListener('asmm_donation_received', handleUpdate);
      if (cycleTimeoutRef.current) clearTimeout(cycleTimeoutRef.current);
    };
  }, []);

  // Non-intrusive delayed cycle logic
  useEffect(() => {
    if (donations.length === 0 || isDismissed) return;

    // Step 1: Initial Delay on Mount
    const initialTimer = setTimeout(() => {
      if (appearanceCountRef.current < MAX_SESSION_APPEARANCES) {
        setIsVisible(true);
        appearanceCountRef.current += 1;
      }
    }, INITIAL_DELAY_MS);

    return () => clearTimeout(initialTimer);
  }, [donations.length, isDismissed]);

  // Handle display duration & quiet rest interval cycle
  useEffect(() => {
    if (!isVisible || isPaused || isDismissed) return;

    // After DISPLAY_DURATION_MS, hide and schedule next appearance after QUIET_INTERVAL_MS
    const hideTimer = setTimeout(() => {
      setIsVisible(false);

      if (appearanceCountRef.current < MAX_SESSION_APPEARANCES) {
        cycleTimeoutRef.current = setTimeout(() => {
          if (!isDismissed) {
            setCurrentIndex((prev) => (prev + 1) % donations.length);
            setIsVisible(true);
            appearanceCountRef.current += 1;
          }
        }, QUIET_INTERVAL_MS);
      }
    }, DISPLAY_DURATION_MS);

    return () => clearTimeout(hideTimer);
  }, [isVisible, isPaused, isDismissed, donations.length]);

  // Strict check: Only show on /donations, /donasi, or their sub pages
  const isDonationsPage = typeof window !== 'undefined' && (
    window.location.pathname.startsWith('/donations') ||
    window.location.pathname.startsWith('/donasi')
  );

  if (donations.length === 0 || isDismissed || !isDonationsPage) return null;

  const current = donations[currentIndex] || donations[0];
  const displayName = current.isAnonymous ? 'Hamba Allah' : current.donorName;
  const initial = current.isAnonymous ? 'HA' : displayName.charAt(0).toUpperCase();

  const handleDismiss = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsVisible(false);
    setIsDismissed(true);
    try {
      sessionStorage.setItem('asmm_live_toast_dismissed', 'true');
    } catch { }
    if (cycleTimeoutRef.current) clearTimeout(cycleTimeoutRef.current);
  };

  const handleGoToDonations = () => {
    const targetPath = current?.campaignId ? `/donations/${current.campaignId}` : '/donations';
    window.history.pushState(null, '', targetPath);
    window.dispatchEvent(new PopStateEvent('popstate'));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const positionClasses = hasBottomNav
    ? 'bottom-16 left-3 max-w-[calc(100%-4.5rem)] sm:bottom-4 sm:left-4 sm:max-w-sm sm:w-auto'
    : 'bottom-4 left-3 max-w-[calc(100%-1.5rem)] sm:bottom-4 sm:left-4 sm:max-w-sm sm:w-auto';

  return (
    <div
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      className={`fixed z-40 w-auto transition-all duration-700 ease-out transform pointer-events-auto ${positionClasses} ${
        isVisible ? 'translate-y-0 opacity-100 scale-100' : 'translate-y-6 opacity-0 scale-95 pointer-events-none'
      }`}
    >
      {/* MOBILE VIEW: Single line detail (bg red, text white) */}
      <div
        onClick={handleGoToDonations}
        className="sm:hidden bg-asmm-red text-white px-3.5 py-2 rounded-full shadow-2xl border border-white/20 flex items-center justify-between gap-2 cursor-pointer active:scale-95 transition-all select-none"
        title="Klik untuk lihat tabung sumbangan"
      >
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <div className="p-1 bg-white/20 rounded-full shrink-0 flex items-center justify-center">
            <Heart className="w-3 h-3 text-white fill-white animate-pulse" />
          </div>
          <p className="text-xs text-white truncate min-w-0 leading-tight">
            <span className="font-extrabold">{displayName}</span>
            <span className="font-medium text-white/90"> menyumbang </span>
            <span className="font-black text-white">RM{current.amount}</span>
          </p>
        </div>

        <button
          type="button"
          onClick={handleDismiss}
          className="text-white/80 hover:text-white p-0.5 rounded-full hover:bg-white/20 transition-colors shrink-0 cursor-pointer"
          title="Tutup"
          aria-label="Tutup"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* DESKTOP VIEW: Rich preview card */}
      <div
        onClick={handleGoToDonations}
        className="hidden sm:block bg-white/95 backdrop-blur-md rounded-2xl p-3 border border-slate-200/90 shadow-lg space-y-1.5 hover:border-red-200 hover:shadow-xl transition-all cursor-pointer group"
      >
        {/* Top meta bar */}
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5">
            <span className="relative flex h-1.5 w-1.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500"></span>
            </span>
            <span className="text-[9px] font-extrabold uppercase tracking-wider text-slate-400 flex items-center gap-1">
              <Sparkles className="w-2.5 h-2.5 text-amber-500" />
              Infaq Terkini
            </span>
          </div>

          <button
            type="button"
            onClick={handleDismiss}
            className="text-slate-400 hover:text-slate-700 p-0.5 rounded-md transition-colors cursor-pointer"
            title="Tutup"
          >
            <X className="w-3 h-3" />
          </button>
        </div>

        {/* Content Body */}
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-lg bg-red-50 border border-red-100 text-asmm-red font-black text-[10px] flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform">
            {initial}
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-baseline justify-between gap-1">
              <span className="text-[11px] font-extrabold text-slate-900 truncate group-hover:text-asmm-red transition-colors">
                {displayName}
              </span>
              <span className="text-[11px] font-black text-asmm-red shrink-0">
                {formatCurrencyMYR(current.amount)}
              </span>
            </div>

            <div className="text-[10px] text-slate-500 truncate">
              {current.campaignTitle}
            </div>
          </div>
        </div>

        {/* Compact Prayer Snippet */}
        {current.message && (
          <div className="text-[10px] text-slate-600 bg-slate-50 px-2 py-0.5 rounded-md border border-slate-100 italic truncate">
            "{current.message}"
          </div>
        )}
      </div>
    </div>
  );
};
